import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WalletSummary } from "./WalletSummary";
import { WalletPacks } from "./WalletPacks";
import { WalletPrices } from "./WalletPrices";
import { WalletLedger } from "./WalletLedger";
import { WalletLimits } from "./WalletLimits";
import { WalletAdmin } from "./WalletAdmin";
import { BillingReturn } from "./BillingReturn";
import { BillingProfile } from "./BillingProfile";

const NOTICE = {
  simulated: ["border-amber-300 bg-amber-50 text-amber-950", "Ambiente di sviluppo: pagamenti simulati, nessun addebito reale. Prezzi e crediti sono provvisori finché non vengono validati."],
  stripe_test: ["border-sky-300 bg-sky-50 text-sky-950", "Modalità di prova: pagamenti di test, nessun addebito reale."],
  off: ["border-slate-300 bg-white text-slate-800", "I pagamenti del piano non sono ancora attivi: nessun addebito è possibile per ora."],
};

function Section({ value, title, hint, children }) {
  return (
    <AccordionItem value={value} className="rounded-xl border border-slate-200 bg-white px-4 sm:px-5" data-testid={`wallet-section-${value}`}>
      <AccordionTrigger className="py-4 text-left hover:no-underline" data-testid={`wallet-section-${value}-toggle`}>
        <span><span className="block text-base font-semibold text-slate-900">{title}</span>{hint && <span className="mt-0.5 block text-sm font-normal text-slate-600">{hint}</span>}</span>
      </AccordionTrigger>
      <AccordionContent className="pb-5">{children}</AccordionContent>
    </AccordionItem>);
}

// ONE balance in credits for AI, email and WhatsApp API. Summary first; explanations and advanced settings in sections closed by default.
export default function AreaWallet({ tid, role, tenant }) {
  const api = useApi();
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const perms = tenant?.perms || [];
  const can = (p) => role === "support" || perms.includes(p);
  const load = useCallback(() => api.get(`/t/${tid}/wallet`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [api, tid]);
  useEffect(() => { load(); }, [load, tick]);
  const refresh = () => setTick((x) => x + 1);
  const buy = async (kind, pack_id, extra = {}) => {
    try {
      const { data } = await api.post(`/t/${tid}/billing/checkout`, { kind, pack_id, origin_url: window.location.origin, ...extra });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Loader2 className="animate-spin text-slate-500" />;
  const manage = can("wallet.manage");
  const [cls, txt] = NOTICE[d.billing_mode] || NOTICE.off;
  return (
    <div className="space-y-4" data-testid="area-wallet">
      <BillingReturn tid={tid} onDone={refresh} />
      <p className={`rounded-lg border px-4 py-2.5 text-sm ${cls}`} data-testid="wallet-dev-notice">{txt}</p>
      <WalletSummary d={d} manage={manage} onBuyPlan={(consent) => buy("subscription", undefined, consent)} tid={tid} onChanged={refresh} />
      <BillingProfile tid={tid} manage={manage} />
      <Accordion type="multiple" className="space-y-3" data-testid="wallet-sections">
        <Section value="packs" title="Ricariche" hint={d.packs_blocked ? "Disponibili con l'abbonamento attivo" : "Pacchetti di crediti aggiuntivi"}>
          <WalletPacks packs={d.packs} provisional={d.prices.provisional} creditEur={d.prices.credit_eur} manage={manage} blocked={d.packs_blocked} onBuy={(id) => buy("pack", id)} />
        </Section>
        <Section value="prices" title="Come funzionano i crediti" hint="Cosa consuma crediti e quanto costa ogni operazione">
          <WalletPrices prices={d.prices} waEnabled={d.whatsapp_billing_enabled} tid={tid} />
        </Section>
        {manage && <Section value="limits" title="Limiti di spesa" hint="Tetto mensile e per singola operazione">
          <WalletLimits tid={tid} limits={d.limits} active={d.active} onSaved={refresh} /></Section>}
        {can("payments.read") && <Section value="ledger" title="Movimenti" hint="Ricariche, consumi e scadenze dei crediti">
          <WalletLedger tid={tid} tick={tick} /></Section>}
        {role === "support" && <Section value="admin" title="Strumenti dell'assistenza MUSORA" hint="Visibili solo allo staff">
          <WalletAdmin tid={tid} d={d} onChanged={refresh} /></Section>}
      </Accordion>
    </div>);
}
